import { supabase } from '@/shared/lib/supabase';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * Plan-change saga client (Phase 8c.4 / 8c.5).
 *
 *   quote()   → read-only proration preview (no DB writes)
 *   confirm() → inserts plan_change_request + invoice (upgrade) OR applies
 *               immediately (downgrade / no_change). Idempotent via Class A.
 *
 * Errors arrive as EdgeFunctionError so callers can branch on .code:
 *   no_change · plan_change_already_pending · incompatible_billing_cycle
 *   customer_link_period_unset · new_plan_inactive · etc.
 */

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>,
  idempotencyKey?: string
): Promise<TRes> {
  const key = idempotencyKey ?? crypto.randomUUID();
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': key },
  });

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code && parsed.message) {
          throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
        }
      } catch (parseErr) {
        if (parseErr instanceof EdgeFunctionError) throw parseErr;
      }
    }
    throw new Error(error.message);
  }

  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new EdgeFunctionError(apiErr.code, apiErr.message, 200, apiErr.details);
  }

  return data as TRes;
}

export type PlanChangeKind = 'upgrade' | 'downgrade' | 'no_change';

export interface PlanChangeQuote {
  kind: PlanChangeKind;
  effective_date: string;
  current_plan: {
    id: string;
    slug: string;
    name: string;
    recurring_amount: number;
    whm_package_name: string;
    billing_cycle: string;
  };
  new_plan: {
    id: string;
    slug: string;
    name: string;
    recurring_amount: number;
    whm_package_name: string;
    billing_cycle: string;
  };
  period: {
    start: string;
    end: string;
    days_total: number;
    days_remaining: number;
  };
  proration: {
    credit_for_unused_current: number;
    charge_for_new_period: number;
    net_charge: number;
  };
  applies_immediately: boolean;
  message: string;
  /**
   * Sprint C C-4 — populated when an upgrade would breach the reseller oversell
   * ratio. UI surfaces a warning above the confirm button; the confirm EF still
   * refuses outright if the breach holds at confirm time.
   */
  capacity_warning?: {
    reason: 'account_cap_breach' | 'disk_oversell_breach' | string;
    current_state: unknown;
  };
}

export interface PlanChangeConfirmInput {
  customer_link_id: string;
  new_plan_id: string;
  effective_date?: string;
  notes?: string;
}

export interface PlanChangeConfirmResult {
  ok: true;
  plan_change_request_id: string;
  kind: PlanChangeKind;
  invoice_id: string | null;
  applies_immediately: boolean;
  proration: {
    credit_for_unused_current: number;
    charge_for_new_period: number;
    net_charge: number;
  };
}

export const adminPlanChangeService = {
  async quote(input: {
    customer_link_id: string;
    new_plan_id: string;
    effective_date?: string;
  }): Promise<PlanChangeQuote> {
    return invokeEdgeFunction<PlanChangeQuote>('plan-change-quote', input);
  },

  async confirm(input: PlanChangeConfirmInput): Promise<PlanChangeConfirmResult> {
    return invokeEdgeFunction<PlanChangeConfirmResult>(
      'plan-change-confirm',
      input as unknown as Record<string, unknown>
    );
  },
};
